<template>
    <div class="crud-table mt-3">

        <!-- create-new button (if enabled) -->

        <b-btn class="float-right" variant="primary"
               v-if="newItem"
               @click="createItem()">
            Create New {{ itemName }}
        </b-btn>

        <h1>{{ title }}</h1>

        <data-table ref="dataTable"
                    :show-filters="true"
                    :show-pager="true"
                    :show-details="showDetails"
                    :get-items="getItems"
                    :filters="filters"
                    :fields="fields">

            <!-- template for custom columns -->
            <template v-slot:customField="slotProps">
                <div v-if="slotProps.row.field.key === 'actions'">
                    <b-btn class="btn btn-secondary btn-sm mr-2"
                           @click="editItem(slotProps.row.item)">
                        <b-icon-pencil-square/>
                    </b-btn>
                    <b-btn size="sm"
                           variant="danger"
                           @click="deleteItem(slotProps.row.item)">
                        <b-icon-trash/>
                    </b-btn>
                </div>
            </template>

            <!-- template for custom row details -->
            <template v-slot:customRowDetails="slotProps">
                <!-- parent component can customize this via a template, same as in DataTable... -->
                <slot name="customRowDetails" v-bind:row="slotProps.row"/>
            </template>

        </data-table>

        <!-- edit modal-->

        <b-modal hide-footer
                 id="editModal"
                 :title="`${editMode === 'UPDATE' ? 'Update' : 'Create'} ${itemName}`">

            <!-- edit fields -->

            <b-form-group v-for="(f,i) in editFields"
                          :key="i"
                          :label="f.label">

                <!-- text input -->
                <b-form-input v-if="f.type === 'text'"
                              type="text"
                              v-model="editingItem[f.key]"/>

                <!-- number input -->
                <b-form-input v-if="f.type === 'number'"
                              type="number"
                              v-model="editingItem[f.key]"/>

                <!-- email input -->
                <b-form-input v-if="f.type === 'email'"
                              type="email"
                              v-model="editingItem[f.key]"/>

                <!-- textarea -->
                <b-form-textarea v-if="f.type === 'textarea'"
                                 v-model="editingItem[f.key]"/>

                <!-- select -->
                <b-form-select v-if="f.type === 'select'"
                               :options="f.options"
                               v-model="editingItem[f.key]"/>

            </b-form-group>

            <b-btn variant="primary" class="float-right"
                   @click="saveEditingItem()">
                Save
            </b-btn>
        </b-modal>

    </div>
</template>

<!-- ----------------------------------------------------------------------- -->

<script>

/**
 * Builds on DataTable to provide a complete CRUD view for simple admin-type operations on a given model
 * - including a standard Actions column and a modal for creating, updating or deleting items.
 * NB: Does not include any validation in the modal. Custom column templates are also not supported.
 */

import Vue from 'vue'
import * as _ from 'lodash'

export default Vue.extend({
    name: 'CrudTable',
    props: {
        title: String,
        itemTable: String,
        itemName: String,
        newItem: Object,
        showDetails: Boolean,
        filters: Object,
        displayFields: Array,
        editFields: Array,
    },
    data () {
        return {
            editingItem: {},
            editMode: null, // 'UPDATE' / 'CREATE'
            fields: _.concat(this.displayFields, [
                { key: 'actions', sortable: false, custom: true },
            ]),
        }
    },
    methods: {
        getItems (listParams) {
            return this.$api.getItems(this, this.itemTable, this.filters, listParams)
        },
        createItem () {
            this.editingItem = _.cloneDeep(this.newItem)
            this.editMode = 'CREATE'
            this.$bvModal.show('editModal')
        },
        editItem (item) {
            this.editingItem = item
            this.editMode = 'UPDATE'
            this.$bvModal.show('editModal')
        },
        saveEditingItem () {
            if (this.editMode === 'UPDATE') {
                this.$api.updateItem(this, this.itemTable, this.editingItem)
                    .then(() => {
                            this.$bvModal.hide('editModal')
                            this.refresh()
                            this.$toasted.info(`${this.itemName} updated`)
                        },
                        (error) => {
                            this.$toasted.error(`Could not update ${this.itemName}`)
                        })
            } else if (this.editMode === 'CREATE') {
                this.$api.createItem(this, this.itemTable, this.editingItem)
                    .then(() => {
                            this.$bvModal.hide('editModal')
                            this.refresh()
                            this.$toasted.info(`${this.itemName} created`)
                        },
                        (error) => {
                            this.$toasted.error(`Could not create ${this.itemName}`)
                        })
            }
        },
        deleteItem (item) {
            if (confirm(`Delete this ${this.itemName} permanently?`)) {
                this.$api.deleteItem(this, this.itemTable, item)
                    .then((response) => {
                        this.$toasted.info(`${this.itemName} deleted`)
                        this.refresh()
                    })
            }
        },
        refresh () {
            this.$refs.dataTable.refresh()
        },
    },
})

</script>

<!-- ----------------------------------------------------------------------- -->

<style lang="scss">

</style>
